import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { PaginatedListDto } from '../models/common/paginated-list.dto';
import type { AssetChargeInput } from '../models/assets/asset-charges.dto';
import type {
  AssetAssignmentDto,
  AssetCategoryDto,
  AssetDetailsDto,
  AssetDto,
  AssetMaintenanceDto,
  AssetStatus,
  AssignAssetDto,
  CompleteAssetMaintenanceDto,
  CreateAssetCategoryDto,
  CreateAssetDto,
  CreateAssetMaintenanceDto,
  ReturnAssetDto,
  UpdateAssetCategoryDto,
  UpdateAssetDto,
} from '../models/assets/assets.dto';

export type AssetListQuery = {
  skip: number;
  rowCount: number;
  search?: string;
  categoryId?: string;
  status?: AssetStatus | '';
  branchId?: string;
  assignedToUserId?: string;
};

@Injectable({ providedIn: 'root' })
export class AssetsService {
  private readonly http = inject(HttpClient);

  private apiUrl(path: string): string {
    const base = environment.apiBaseUrl.replace(/\/+$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  getAssets(query: AssetListQuery): Observable<PaginatedListDto<AssetDto>> {
    let params = new HttpParams().set('skip', String(query.skip)).set('rowCount', String(query.rowCount));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    if (query.categoryId?.trim()) params = params.set('categoryId', query.categoryId.trim());
    if (query.status) params = params.set('status', String(query.status));
    if (query.branchId?.trim()) params = params.set('branchId', query.branchId.trim());
    if (query.assignedToUserId?.trim()) params = params.set('assignedToUserId', query.assignedToUserId.trim());
    return this.http.get<PaginatedListDto<AssetDto>>(this.apiUrl('/api/Assets'), { params });
  }

  getCategories(): Observable<PaginatedListDto<AssetCategoryDto>> {
    return this.http.get<PaginatedListDto<AssetCategoryDto>>(this.apiUrl('/api/AssetCategories'), {
      params: new HttpParams().set('skip', '0').set('rowCount', '200'),
    });
  }

  getActiveCategories(): Observable<AssetCategoryDto[]> {
    return this.http.get<AssetCategoryDto[]>(this.apiUrl('/api/AssetCategories/active'));
  }

  createCategory(model: CreateAssetCategoryDto): Observable<AssetCategoryDto> {
    return this.http.post<AssetCategoryDto>(this.apiUrl('/api/AssetCategories'), model);
  }

  updateCategory(model: UpdateAssetCategoryDto): Observable<AssetCategoryDto> {
    return this.http.put<AssetCategoryDto>(this.apiUrl('/api/AssetCategories'), model);
  }

  deleteCategory(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/AssetCategories/${encodeURIComponent(id)}`));
  }

  activateCategory(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/AssetCategories/${encodeURIComponent(id)}/activate`), {});
  }

  deactivateCategory(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/AssetCategories/${encodeURIComponent(id)}/deactivate`), {});
  }

  getAsset(id: string): Observable<AssetDetailsDto> {
    return this.http.get<AssetDetailsDto>(this.apiUrl(`/api/Assets/${encodeURIComponent(id)}`));
  }

  createAsset(model: CreateAssetDto): Observable<AssetDto> {
    return this.http.post<AssetDto>(this.apiUrl('/api/Assets'), model);
  }

  updateAsset(model: UpdateAssetDto): Observable<AssetDto> {
    return this.http.put<AssetDto>(this.apiUrl('/api/Assets'), model);
  }

  retire(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/Assets/${encodeURIComponent(id)}/retire`), {});
  }

  markLost(id: string, charge?: AssetChargeInput): Observable<boolean> {
    return this.markAsset(id, 'lost', charge);
  }

  markDamaged(id: string, charge?: AssetChargeInput): Observable<boolean> {
    return this.markAsset(id, 'damaged', charge);
  }

  /** Marks the assignment lost and charges the employee in the same step (needs Assets.Manage and Assets.ChargeManage). */
  markAssignmentLostWithCharge(assignmentId: string, charge: AssetChargeInput): Observable<AssetAssignmentDto> {
    return this.http.post<AssetAssignmentDto>(this.apiUrl(`/api/AssetAssignments/${encodeURIComponent(assignmentId)}/mark-lost-with-charge`), charge);
  }

  markAssignmentDamagedWithCharge(assignmentId: string, charge: AssetChargeInput): Observable<AssetAssignmentDto> {
    return this.http.post<AssetAssignmentDto>(this.apiUrl(`/api/AssetAssignments/${encodeURIComponent(assignmentId)}/mark-damaged-with-charge`), charge);
  }

  private markAsset(id: string, kind: 'lost' | 'damaged', charge?: AssetChargeInput): Observable<boolean> {
    const base = `/api/Assets/${encodeURIComponent(id)}/mark-${kind}`;
    return charge
      ? this.http.post<boolean>(this.apiUrl(`${base}-with-charge`), charge)
      : this.http.post<boolean>(this.apiUrl(base), {});
  }

  sendToMaintenance(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/Assets/${encodeURIComponent(id)}/send-to-maintenance`), {});
  }

  completeAssetMaintenance(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/Assets/${encodeURIComponent(id)}/complete-maintenance`), {});
  }

  /** `returnRequestedOnly`: the pending return requests employees sent from mobile, newest request first. */
  getAssignments(options: { returnRequestedOnly?: boolean } = {}): Observable<PaginatedListDto<AssetAssignmentDto>> {
    let params = new HttpParams().set('skip', '0').set('rowCount', '50');
    if (options.returnRequestedOnly) params = params.set('returnRequestedOnly', 'true');
    return this.http.get<PaginatedListDto<AssetAssignmentDto>>(this.apiUrl('/api/AssetAssignments'), { params });
  }

  assignAsset(model: AssignAssetDto): Observable<AssetAssignmentDto> {
    return this.http.post<AssetAssignmentDto>(this.apiUrl('/api/AssetAssignments'), model);
  }

  returnAsset(id: string, model: ReturnAssetDto): Observable<AssetAssignmentDto> {
    return this.http.post<AssetAssignmentDto>(this.apiUrl(`/api/AssetAssignments/${encodeURIComponent(id)}/return`), model);
  }

  confirmReturn(id: string): Observable<AssetAssignmentDto> {
    return this.http.post<AssetAssignmentDto>(this.apiUrl(`/api/AssetAssignments/${encodeURIComponent(id)}/confirm-return`), {});
  }

  getMaintenances(): Observable<PaginatedListDto<AssetMaintenanceDto>> {
    return this.http.get<PaginatedListDto<AssetMaintenanceDto>>(this.apiUrl('/api/AssetMaintenances'), {
      params: new HttpParams().set('skip', '0').set('rowCount', '50'),
    });
  }

  scheduleMaintenance(model: CreateAssetMaintenanceDto): Observable<AssetMaintenanceDto> {
    return this.http.post<AssetMaintenanceDto>(this.apiUrl('/api/AssetMaintenances'), model);
  }

  completeMaintenance(id: string, model: CompleteAssetMaintenanceDto): Observable<AssetMaintenanceDto> {
    return this.http.post<AssetMaintenanceDto>(this.apiUrl(`/api/AssetMaintenances/${encodeURIComponent(id)}/complete`), model);
  }

  cancelMaintenance(id: string): Observable<AssetMaintenanceDto> {
    return this.http.post<AssetMaintenanceDto>(this.apiUrl(`/api/AssetMaintenances/${encodeURIComponent(id)}/cancel`), {});
  }
}
